// src/shared/continueLearning.ts
// ─────────────────────────────────────────────────────────────────────────
// Deterministic "Continue learning" resolver for the top nav.
//
// SCOPE (deliberate): NOT a recommendation engine. No scoring, no "because
// you liked X", no school/playlist awareness, no SRS prioritization, no
// simulator signals, no backend sync. It walks existing local progress in a
// fixed priority order and returns the single most sensible next target.
//
// Priority:
//   1. Last active incomplete lesson              → 'resume-lesson'
//   2. Next lesson in the current module           → 'next-lesson'
//   3. First incomplete lesson in current module   → 'next-lesson'
//   4. First incomplete lesson in the next-up
//      Core Path module (corePathPosition.ts)      → 'next-lesson' / 'start-module'
//   5. First incomplete lesson in path order       → 'next-lesson' / 'start-module'
//   6. /learn                                      → 'fallback'
//
// COMPLETION-AWARE, never gate-aware: nothing here (or anywhere) is locked.
// Priority #4 exists because after completing modules by test alone nothing
// is "in progress", but the honest next stop is the next-up module, not
// module 1's unread lessons. Priorities #4 and #5 scan Core Path
// (map-surface) modules only, so school lessons never get recommended as
// the next Core Path step; #1 to #3 still resume school lessons normally
// via last-active.
//
// LABEL RULE (start-module): a fresh module reads 'Start module' ONLY for a
// user with zero completed lessons anywhere. A user with any prior progress
// (e.g. they just finished a module and landed on the next-up untouched one)
// sees 'Start next module' — 'Start module' after weeks of work reads like
// the app forgot them.
//
// "Incomplete" = the strongest existing completion flag the app tracks,
// which today is the per-lesson boolean in useProgress (isLessonComplete).
// When a richer per-lesson state lands (e.g. status !== 'mastered'), only
// the `isComplete` predicate below needs to change.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '@nisk/core/learning';

export type ContinueState =
	| 'resume-lesson'
	| 'next-lesson'
	| 'start-module'
	| 'fallback';

export interface ContinueTarget {
	label: string;
	href: string;
	context?: string;
	state: ContinueState;
}

// Last-active tracking: one localStorage record, written by lesson views.
// Optional: the resolver degrades gracefully (priority #2–#6) when absent.
const LAST_ACTIVE_KEY = 'nis.lastActive';

interface LastActive {
	moduleId: string;
	lessonId: string;
}

export function readLastActive(): LastActive | null {
	try {
		const raw = localStorage.getItem(LAST_ACTIVE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<LastActive>;
		if (parsed.moduleId && parsed.lessonId) {
			return { moduleId: parsed.moduleId, lessonId: parsed.lessonId };
		}
		return null;
	} catch {
		return null;
	}
}

// Call this from a lesson view when a lesson is opened/worked on so that
// priority #1 ("resume last active") can resolve. Cheap and optional.
export function recordLastActiveLesson(
	moduleId: string,
	lessonId: string,
): void {
	try {
		localStorage.setItem(
			LAST_ACTIVE_KEY,
			JSON.stringify({ moduleId, lessonId }),
		);
	} catch {
		/* noop */
	}
}

const lessonHref = (moduleId: string, lessonId: string): string =>
	`/learn/map/${moduleId}/${lessonId}`;

// Tier priority for the ordered scan. Mirrors CurriculumMeta TIER_ORDER
// (Core Path tiers 1–6, legacy 90+) but kept local so this helper has no
// extra coupling; modules are normally already authored in tier+order
// sequence, so we sort defensively.
const TIER_RANK: Record<string, number> = {
	orientation: 1,
	control: 2,
	stability: 3,
	growth: 4,
	'freedom-path': 5,
	'life-design': 6,
	foundation: 90,
	builder: 91,
	freedom: 92,
	advanced: 93,
};

interface ResolveArgs {
	modules: LearningModule[];
	// Strongest existing completion predicate. Defaults to a no-op (treats
	// everything as incomplete) so callers MUST pass the real one from
	// useProgress; kept optional only to make the helper testable.
	isComplete: (moduleId: string, lessonId: string) => boolean;
	lastActiveLessonId?: string | null;
	lastActiveModuleId?: string | null;
	/** The next-up Core Path module id (first module whose final test
	 *  isn't passed). Optional; when absent, priority #4 is skipped. */
	positionModuleId?: string | null;
}

function sortedModules(modules: LearningModule[]): LearningModule[] {
	return [...modules].sort((a, b) => {
		const ta = TIER_RANK[a.tier] ?? 99;
		const tb = TIER_RANK[b.tier] ?? 99;
		if (ta !== tb) return ta - tb;
		return (a.order ?? 0) - (b.order ?? 0);
	});
}

/** Core Path spine only: school-surface modules never enter the ordered
 *  recommendation scan (they're reachable via #1 to #3 resume instead). */
function pathModules(modules: LearningModule[]): LearningModule[] {
	return modules.filter((m) => (m.surface ?? 'map') === 'map');
}

function firstIncompleteIn(
	mod: LearningModule,
	isComplete: (moduleId: string, lessonId: string) => boolean,
	hasAnyCompletion: boolean,
): ContinueTarget | null {
	const lesson = mod.lessons.find((l) => !isComplete(mod.id, l.id));
	if (!lesson) return null;
	const anyDone = mod.lessons.some((l) => isComplete(mod.id, l.id));
	if (anyDone) {
		return {
			label: 'Next lesson',
			href: lessonHref(mod.id, lesson.id),
			context: mod.title,
			state: 'next-lesson',
		};
	}
	// Untouched module. See LABEL RULE in the header: any prior completion
	// anywhere means the user is moving between modules, not starting out.
	return {
		label: hasAnyCompletion ? 'Start next module' : 'Start module',
		href: lessonHref(mod.id, lesson.id),
		context: mod.title,
		state: 'start-module',
	};
}

function fallback(): ContinueTarget {
	return { label: 'Start learning', href: '/learn', state: 'fallback' };
}

export function resolveContinueLearningTarget(
	args: ResolveArgs,
): ContinueTarget {
	const { modules, isComplete } = args;
	if (!modules || modules.length === 0) return fallback();

	const ordered = sortedModules(modules);

	// Any prior completed lesson anywhere → drives the start-module label
	// (see LABEL RULE). Computed once; cheap (short-circuits on first hit).
	const hasAnyCompletion = ordered.some((m) =>
		m.lessons.some((l) => isComplete(m.id, l.id)),
	);

	// Resolve the "current" module: the one referenced by last-active, else
	// (further down) the next-up module, else the first incomplete module.
	const lastModuleId = args.lastActiveModuleId ?? undefined;
	const lastLessonId = args.lastActiveLessonId ?? undefined;

	// ── #1 Last active incomplete lesson ──────────────────────────────────
	if (lastModuleId && lastLessonId) {
		const mod = ordered.find((m) => m.id === lastModuleId);
		const lesson = mod?.lessons.find((l) => l.id === lastLessonId);
		if (mod && lesson && !isComplete(mod.id, lesson.id)) {
			return {
				label: 'Continue lesson',
				href: lessonHref(mod.id, lesson.id),
				context: lesson.title,
				state: 'resume-lesson',
			};
		}

		// ── #2 Next lesson in the current (last-active) module ──────────────
		if (mod) {
			const idx = mod.lessons.findIndex((l) => l.id === lastLessonId);
			if (idx >= 0) {
				const after = mod.lessons
					.slice(idx + 1)
					.find((l) => !isComplete(mod.id, l.id));
				if (after) {
					return {
						label: 'Next lesson',
						href: lessonHref(mod.id, after.id),
						context: mod.title,
						state: 'next-lesson',
					};
				}
			}

			// ── #3 First incomplete lesson anywhere in the current module ─────
			const firstInMod = mod.lessons.find(
				(l) => !isComplete(mod.id, l.id),
			);
			if (firstInMod) {
				return {
					label: 'Next lesson',
					href: lessonHref(mod.id, firstInMod.id),
					context: mod.title,
					state: 'next-lesson',
				};
			}
		}
	}

	const path = pathModules(ordered);

	// ── #4 First incomplete lesson at the next-up Core Path module ─────────
	if (args.positionModuleId) {
		const positionMod = path.find((m) => m.id === args.positionModuleId);
		if (positionMod) {
			const target = firstIncompleteIn(
				positionMod,
				isComplete,
				hasAnyCompletion,
			);
			if (target) return target;
		}
	}

	// ── #5 First incomplete lesson in Core Path order ───────────────────────
	for (const mod of path) {
		const target = firstIncompleteIn(mod, isComplete, hasAnyCompletion);
		if (target) return target;
	}

	// ── #6 Everything complete (or nothing resolvable) ─────────────────────
	return fallback();
}
